<template>
  <v-app>
  <div v-if="!accessStore.loaded && !accessStore.error" class="min-h-screen flex items-center justify-center bg-gray-50">
    <AuthLoader />
  </div>

  <div v-else-if="accessStore.error" class="min-h-screen flex items-center justify-center bg-gray-50 px-4">
    <div class="card max-w-md w-full text-center">
      <h1 class="text-xl font-semibold text-gray-900 m-0">Couldn't load your access</h1>
      <p class="text-sm text-gray-600 mt-2 mb-0">{{ accessStore.error }}</p>
      <div class="mt-6 flex items-center justify-center gap-3">
        <button class="btn-primary" type="button" @click="accessStore.load()">Retry</button>
        <router-link :to="{ name: 'Logout' }" class="btn-secondary inline-block">Sign out</router-link>
      </div>
    </div>
  </div>

  <AccessRequired v-else-if="!hasAnyAccess" />

  <div v-else class="min-h-screen flex bg-gray-50">
    <a
      href="#main-content"
      class="sr-only focus:not-sr-only focus:fixed focus:left-3 focus:top-3 focus:z-[100] focus:rounded-lg focus:bg-white focus:px-4 focus:py-2 focus:text-sm focus:font-semibold focus:text-blue-700 focus:shadow-lg focus:ring-2 focus:ring-blue-500"
      @click.prevent="skipToMain"
    >
      Skip to content
    </a>
    <aside
      class="bg-gray-900 text-white flex flex-col fixed inset-y-0 left-0 z-50 transition-[width] duration-300 ease-out"
      :class="collapsed ? 'w-16' : 'w-64'"
    >
      <div class="border-b border-gray-700 flex items-center" :class="collapsed ? 'px-4 py-4 justify-center' : 'px-4 py-5'">
        <div class="flex items-center gap-3 overflow-hidden">
          <div class="w-8 h-8 bg-blue-500 rounded-lg shrink-0 flex items-center justify-center">
            <svg class="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
              />
            </svg>
          </div>
          <div v-if="!collapsed" class="min-w-0">
            <div class="font-bold text-white text-sm leading-tight whitespace-nowrap">MP Portal</div>
            <div class="text-xs text-gray-400 whitespace-nowrap">Manufacturing Partners Portal</div>
          </div>
        </div>
      </div>

      <button
        class="absolute top-3 -right-3 w-6 h-7 rounded-md bg-gray-700 border border-gray-600 flex items-center justify-center text-gray-300 hover:bg-blue-600 hover:text-white hover:border-blue-600 transition-colors duration-150 ease-out shadow-md z-10"
        :title="collapsed ? 'Expand sidebar' : 'Collapse sidebar'"
        type="button"
        @click="collapsed = !collapsed"
      >
        <svg
          class="w-3 h-3 transition-transform duration-300 ease-out motion-reduce:transition-none"
          :class="collapsed ? 'rotate-180' : ''"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M15 19l-7-7 7-7" />
        </svg>
      </button>

      <nav class="flex-1 py-4 overflow-y-auto overflow-x-hidden" :class="collapsed ? 'px-2 space-y-1' : 'px-3 space-y-1'">
        <template v-for="(group, index) in navGroups" :key="group.label">
          <p v-if="!collapsed" class="px-3 pb-1 text-[11px] font-semibold uppercase tracking-wider text-gray-500" :class="index === 0 ? 'pt-0' : 'pt-4'">{{ group.label }}</p>
          <div v-else-if="index > 0" class="mx-2 my-2 border-t border-gray-700" role="separator" />
          <NavItem v-for="item in group.items" :key="item.to" :to="item.to" :icon="item.icon" :label="item.label" :collapsed="collapsed" />
        </template>
      </nav>

      <div class="border-t border-gray-700" :class="collapsed ? 'px-2 py-3' : 'px-4 py-4'">
        <div class="flex items-center gap-3 overflow-hidden" :class="collapsed ? 'justify-center' : ''">
          <router-link
            :to="{ name: 'Profile' }"
            class="w-8 h-8 rounded-full bg-blue-600 shrink-0 flex items-center justify-center text-white text-xs font-bold"
            :title="collapsed ? userLabel : ''"
          >
            {{ userInitial }}
          </router-link>
          <div v-if="!collapsed" class="min-w-0 flex-1">
            <router-link :to="{ name: 'Profile' }" class="text-sm text-white font-medium whitespace-nowrap hover:underline block truncate">
              {{ userLabel }}
            </router-link>
            <router-link :to="{ name: 'Logout' }" class="text-xs text-gray-400 hover:text-white whitespace-nowrap">Logout</router-link>
          </div>
        </div>
      </div>
    </aside>

    <div class="min-w-0 flex-1 flex flex-col min-h-screen transition-[padding-left] duration-300 ease-out" :class="collapsed ? 'pl-16' : 'pl-64'">
      <main id="main-content" ref="main" class="min-w-0 flex-1 p-3 outline-none sm:p-6" tabindex="-1">
        <router-view v-if="!routeDenied" />
      </main>
    </div>
  </div>
  <UndoToast />
  </v-app>
</template>

<script setup lang="ts">
  import { computed, onBeforeUnmount, ref, watch } from 'vue';
  import { useRoute, useRouter } from 'vue-router';
  import { useAuth0 } from '@auth0/auth0-vue';
  import NavItem from '@/components/common/NavItem.vue';
  import AuthLoader from '@/components/common/AuthLoader.vue';
  import UndoToast from '@/components/common/UndoToast.vue';
  import AccessRequired from '@/views/common/AccessRequired.vue';
  import { useAccess } from '@/composables/useAccess';
  import { visibleNavGroups } from '@/layouts/navGroups';
  import { resolveAccessDenial, type AccessGuardMeta } from '@/router/accessGuard';
  import { useAccessStore } from '@/store/accessStore';

  // Tailwind's sm breakpoint: below it the sidebar collapses.
  const PHONE_WIDTH = '(max-width: 40rem)';

  const route = useRoute();
  const router = useRouter();
  // Collapsed to icons on a phone: expanded it is 256px of a ~390px screen, which leaves the
  // page it is navigating to no room to be read. It follows the viewport (rotation, zoom).
  const phone = window.matchMedia(PHONE_WIDTH);
  const collapsed = ref(phone.matches);
  const onViewportChange = (event: MediaQueryListEvent): void => {
    collapsed.value = event.matches;
  };
  phone.addEventListener('change', onViewportChange);
  onBeforeUnmount(() => phone.removeEventListener('change', onViewportChange));

  const main = ref<HTMLElement | null>(null);
  // Focus moves with the skip link, so the next Tab starts inside the page.
  function skipToMain(): void {
    main.value?.focus();
  }
  const { user } = useAuth0();
  const accessStore = useAccessStore();
  const { hasAnyAccess, hasPermission } = useAccess();
  const navGroups = computed(() => visibleNavGroups(hasPermission));

  // Synchronous gate: reflects whether the current route is denied for the
  // current user, the instant `accessStore.loaded` or the route changes —
  // no async tick where the denied child could mount. False while access
  // isn't loaded yet, since the loader branch is shown then and nothing
  // route-specific is mounted.
  const routeDenied = computed(() => {
    if (!accessStore.loaded) return false;
    const meta = route.meta as AccessGuardMeta;
    return resolveAccessDenial(
      { requiresPermission: meta.requiresPermission },
      { hasPermission },
    );
  });

  // The router's global guard defers its access check when the caller isn't
  // known yet (no auth token, or /me hasn't loaded) — see src/router/index.ts.
  // Once /me actually loads, re-evaluate access for whatever route we ended
  // up on (this covers the deferred initial navigation, e.g. a hard reload
  // on a protected route before Auth0 bootstrap finished). The `routeDenied`
  // computed above already prevents the denied component from mounting;
  // this watcher only fixes up the URL so the address bar lands on /forbidden.
  watch(
    () => accessStore.loaded,
    (loaded) => {
      if (!loaded) return;
      if (route.name === 'Forbidden') return;
      if (routeDenied.value) {
        void router.replace({ name: 'Forbidden' });
      }
    },
    { immediate: true },
  );

  const userLabel = computed(() => user.value?.name ?? user.value?.email ?? 'Account');
  const userInitial = computed(() => userLabel.value.charAt(0).toUpperCase());
</script>
